import { useEffect, useState } from "react";
import { Fingerprint } from "lucide-react";
import { toast } from "sonner";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useVault } from "@/context/VaultContext";
import { biometricAvailable, registerBiometric } from "@/lib/lock";

export function SecurityPanel() {
  const { security, setSecurity, changePin: change, setBiometric } = useVault();
  const [bioOk, setBioOk] = useState(false);
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  useEffect(() => { biometricAvailable().then(setBioOk); }, []);
  const enableBio = async () => {
    try { await setBiometric(await registerBiometric()); toast.success("Fingerprint unlock enabled"); }
    catch (e) { toast.error("Could not register biometric", { description: e.message }); }
  };
  const changePin = async () => {
    if (!/^\d{4}$/.test(next)) return toast.error("New PIN must be 4 digits");
    const err = await change(cur, next);
    if (err) return toast.error(err);
    setCur(""); setNext(""); toast.success("PIN changed", { description: "Your vault key is now protected by the new PIN" });
  };
  return (
    <div className="space-y-6" data-testid="security-panel">
      <div className="space-y-1.5">
        <Label>Auto-lock after inactivity</Label>
        <Select value={String(security.autoLock ?? 0)} onValueChange={(v) => setSecurity({ ...security, autoLock: Number(v) })}>
          <SelectTrigger className="w-48" data-testid="autolock-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {[[0, "Off"], [1, "1 minute"], [5, "5 minutes"], [15, "15 minutes"], [30, "30 minutes"]].map(([v, l]) => <SelectItem key={v} value={String(v)}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-start justify-between gap-4" data-testid="lock-on-hide-row">
        <div>
          <Label htmlFor="lock-on-hide">Panic lock when app is hidden</Label>
          <p className="text-xs text-muted-foreground">Locks instantly when you switch apps or tabs (not while picking a file). Press Esc twice or tap Panic Lock anytime.</p>
        </div>
        <Switch id="lock-on-hide" checked={security.lockOnHide !== false} onCheckedChange={(v) => setSecurity({ ...security, lockOnHide: v })} data-testid="lock-on-hide-switch" />
      </div>
      <div className="space-y-2">
        <Label>Fingerprint / Biometric unlock</Label>
        <p className="text-xs text-muted-foreground" data-testid="biometric-note">Convenience only — your PIN always works as fallback. While enabled, a device-bound copy of the vault key is kept on this device, which weakens encryption at rest.{!bioOk && " No platform authenticator detected on this device/browser."}</p>
        {security.bioId
          ? <Button variant="outline" onClick={() => setBiometric(null)} data-testid="biometric-disable-button"><Fingerprint className="h-4 w-4 mr-1" />Disable biometric</Button>
          : <Button variant="outline" onClick={enableBio} disabled={!bioOk} data-testid="biometric-enable-button"><Fingerprint className="h-4 w-4 mr-1" />Enable biometric</Button>}
      </div>
      <div className="space-y-2">
        <Label>Change PIN</Label>
        <div className="flex flex-wrap gap-2">
          <Input type="password" inputMode="numeric" maxLength={4} placeholder="Current" value={cur} onChange={(e) => setCur(e.target.value)} className="w-28" data-testid="current-pin-input" />
          <Input type="password" inputMode="numeric" maxLength={4} placeholder="New" value={next} onChange={(e) => setNext(e.target.value)} className="w-28" data-testid="new-pin-input" />
          <Button variant="secondary" onClick={changePin} data-testid="change-pin-button">Update</Button>
        </div>
      </div>
    </div>
  );
}
